import {Component, OnInit, TemplateRef} from '@angular/core';
import {QuestionnaireShort} from "../model/questionnaire-short";
import {MessageService} from "../service/message.service";
import {ContentService} from "../service/content.service";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {PlaylistService} from "../service/playlist.service";
import {QuestionnaireService} from "../service/questionnaire.service";
import {RecommendationService} from "../service/recommendation.service";
import {AnswerService} from "../service/answer.service";
import {Id} from "../model/id";
import {Question} from "../model/question";
import {BsModalRef, BsModalService, ModalOptions} from "ngx-bootstrap";
import {Recommendation, RecommendationShort} from "../model/recommendation";
import {Answer} from "../model/answer";
import {ComponentHasOpenedModal} from "../modal-deactivation.guard";
import {
  BY_ANSWER_EXPLANATION_TYPE,
  ITERABLE_OPERATORS,
  RECOMMENDATION_SUPPORTED,
  RECOMMENDATION_SUPPORTED_ANSWER_VALUES,
  RECOMMENDATION_TYPES,
  TEXT_EXPLANATION_TYPE,
  YES_LABEL
} from "../constants";
import {AnswerType} from "../model/answer-type";
import {RecommendationItem} from "../model/recommendation-item";
import {RecommendationClause, RecommendationClauseShort} from "../model/recommendation-clause";
import {ExplanationFull, ExplanationQuestionFull, ExplanationShort, ExplanationTextFull} from "../model/explanation";
import {RecommendationEditWizardComponent} from "../recommendation-edit-wizard/recommendation-edit-wizard.component";
import {PlaylistInfo} from "../model/playlist-info";

@Component({
  selector: 'app-questionnaire-edit',
  templateUrl: './questionnaire-edit.component.html',
  styleUrls: ['./questionnaire-edit.component.css']
})
export class QuestionnaireEditComponent implements OnInit, ComponentHasOpenedModal {

  private maxTitleLength = 256;
  private maxDescriptionLength = 2048;
  private maxQuestionTextLength = 256;

  private modalConfig: ModalOptions = {backdrop: 'static', keyboard: false};

  editQuesitonModalRef: BsModalRef;
  editQuestionData: Question;

  availableRecommendationTypes: string[] = RECOMMENDATION_TYPES;
  availableQuestionOperators: string[] = ITERABLE_OPERATORS;

  questionnaireId: string = null;
  questionnaire: QuestionnaireShort = new QuestionnaireShort(null, "", "");
  titleFieldError: string = null;
  descriptionFieldError: string = null;
  questionTextFieldError: string = null;
  questions: Question[] = [];
  recommendations: Recommendation[] = [];
  availableAnswers: Answer[];
  availableRecommendationAnswerTypes: AnswerType[];
  editRecommendationModalRef: BsModalRef;
  private playlists: PlaylistInfo[];

  constructor(private messageService: MessageService,
              private questionnaireService: QuestionnaireService,
              private answerService: AnswerService,
              private recommendationService: RecommendationService,
              private playlistService: PlaylistService,
              private contentService: ContentService,
              private route: ActivatedRoute,
              private modalService: BsModalService) {
  }

  public hasOpenedModal(): boolean {
    return this.editQuesitonModalRef != null || this.editRecommendationModalRef != null;
  }

  ngOnInit() {
    this.availableAnswers = this.answerService.answers();
    this.availableRecommendationAnswerTypes = RECOMMENDATION_SUPPORTED;

    let paramMap = this.route.snapshot.paramMap;
    this.initQuestionnaire(paramMap);
    this.playlistService.list().toPromise().then(v => this.playlists = v).catch(this.onRejected());
  }

  onSubmit() {
    if (this.questionnaire.title == null || this.questionnaire.title.length == 0) {
      this.titleFieldError = "This field is required.";
    } else if (this.questionnaire.title.length > this.maxTitleLength) {
      this.titleFieldError = "You have exceeded the input length.";
    } else {
      this.titleFieldError = null;
    }

    if (this.questionnaire.description == null || this.questionnaire.description.length == 0) {
      this.descriptionFieldError = "This field is required.";
    } else if (this.questionnaire.description.length > this.maxDescriptionLength) {
      this.descriptionFieldError = "You have exceeded the input length.";
    } else {
      this.descriptionFieldError = null;
    }

    if (this.titleFieldError != null
      || this.descriptionFieldError != null) {
      return;
    }

    let savePromise: Promise<Id> = this.questionnaireId == null ?
      this.questionnaireService.addQuestionnaire(this.questionnaire.title, this.questionnaire.description).toPromise() :
      this.questionnaireService.updateQuestionnaire(this.questionnaire).toPromise();

    savePromise.then(id => this.loadById(id.id)).catch(this.onRejected());
  }

  usedInRecommendation(question: Question): boolean {
    return question && this.recommendations &&
      this.recommendations.some(r => r.id && r.useQuestion(question));
  }

  compareAnswers(a1: Answer, a2: Answer): boolean {
    return a1 && a2 && a1.type == a2.type;
  }

  private initQuestionnaire(paramMap: ParamMap) {
    if (paramMap.has("id")) {
      this.loadById(paramMap.get("id"));
    }
  }

  private loadById(questionnaireId: string) {
    this.questionnaireService.getQuestionnaire(questionnaireId).toPromise()
      .then(value => {
        this.questionnaireId = questionnaireId;
        this.questionnaire = value;
        return this.questionnaireService.getQuestions(this.questionnaireId).toPromise();
      })
      .then(value => {
        this.questions = value;
        return this.recommendationService.list(this.questionnaireId).toPromise();
      })
      .then(value => {
        this.recommendations = this.arrayShortToFull(value);
      })
      .catch(this.onRejected());
  }

  private onRejected() {
    return err => {
      this.messageService.addError('error occurred', err);
      console.log(err);
    }
  }

  removeQuestion(question: Question) {
    if (this.usedInRecommendation(question)) {
      alert("Question is used in recommendation clauses");
      return;
    }
    if (confirm("Do you want to remove this question?")) {
      this.questionnaireService.deleteQuestion(question).toPromise()
        .then(() => this.loadById(this.questionnaireId))
        .catch(this.onRejected());
    }
  }

  editQuestion(question: Question, editQuestionTemplate: TemplateRef<any>) {
    this.editQuestionData = new Question(question.id, question.position, question.text, question.answer);
    this.editQuesitonModalRef = this.modalService.show(editQuestionTemplate, this.modalConfig);
  }

  createQuestion(editQuestionTemplate) {
    this.editQuestionData = new Question(null, 0, '', this.answerService.defaultAnswer());
    this.editQuesitonModalRef = this.modalService.show(editQuestionTemplate, this.modalConfig);
  }

  onSubmitQuestionEdit() {
    if (this.editQuestionData.text == null || this.editQuestionData.text.length == 0) {
      this.questionTextFieldError = "This field is required.";
    } else if (this.editQuestionData.text.length > this.maxQuestionTextLength) {
      this.questionTextFieldError = "You have exceeded the input length.";
    } else {
      this.questionTextFieldError = null;
    }

    if (this.questionTextFieldError != null) {
      return;
    }

    let savePromise: Promise<Id> = this.editQuestionData.id == null ?
      this.questionnaireService.addQuestion(this.editQuestionData.text, this.questionnaireId, this.editQuestionData.answer).toPromise() :
      this.questionnaireService.updateQuestion(this.editQuestionData).toPromise();

    savePromise.then(() => {
      this.loadById(this.questionnaireId);
      this.hideQuestionEdit();
    }).catch(this.onRejected());
  }

  onChangeQuestionPosition(sourceQuestion: Question, newPosition: number) {
    let onPlaceOf = this.questions.find(q => q.position == newPosition);
    if (onPlaceOf == null) {
      return;
    }
    console.log(`move ${sourceQuestion.text} [${sourceQuestion.position}] to ${onPlaceOf.text} [${onPlaceOf.position}]`);
    if (sourceQuestion.id != onPlaceOf.id) {
      this.questionnaireService.move(sourceQuestion.id, onPlaceOf.id).toPromise()
        .then(() => this.loadById(this.questionnaireId))
        .catch(this.onRejected());
    }
  }

  createRecommendation() {
    let recommendation = new Recommendation([], [], this.availableRecommendationTypes[0], null);
    let selectedAnswer = YES_LABEL;
    let questionOperator = this.availableQuestionOperators[0];
    let selectedQuestions = [];
    let explanationSummary = null;
    let explanationsByAnswers = [];
    this.showRecommendationWizard(recommendation, selectedAnswer, questionOperator, selectedQuestions,
      explanationSummary, explanationsByAnswers);
  }

  editRecommendation(recommendation: Recommendation) {

    let selectedAnswer = YES_LABEL;
    let questionOperator = this.availableQuestionOperators[0];
    let selectedQuestions = [];
    let explanationSummary = null;
    let explanationsByAnswers = [];

    if (recommendation.clauses.length > 0) {
      let defaultClause = recommendation.clauses[0];
      selectedAnswer = defaultClause.value.label;
      questionOperator = defaultClause.questionOperator;
      selectedQuestions = defaultClause.questions;

      let textExplanation = recommendation.explanations.find(e => e.type == TEXT_EXPLANATION_TYPE);
      if (textExplanation != null) {
        explanationSummary = textExplanation.value.toShort();
      }
      explanationsByAnswers = recommendation.explanations
        .filter(e => e.type == BY_ANSWER_EXPLANATION_TYPE)
        .map(e => {
          let val: ExplanationQuestionFull = e.value;
          let question = this.questions.find(q => q.id == val.question_id);
          let playlist = this.playlists.find(p => p.id == val.playlist_id);
          return {question, text: val.text, playlist}
        });
    }

    this.showRecommendationWizard(recommendation, selectedAnswer, questionOperator, selectedQuestions, explanationSummary, explanationsByAnswers);
  }

  private showRecommendationWizard(recommendation: Recommendation, selectedAnswer, questionOperator, selectedQuestions, explanationSummary, explanationsByAnswers) {
    let initialState = {
      recommendationId: recommendation.id,
      recommendationType: recommendation.type,
      recommendationDescription: recommendation.description,
      selectedAnswer,
      editRecommendationQuestionOperator: questionOperator,
      selectedQuestions,
      questions: this.questions,
      explanationSummary,
      explanationsByAnswers,
      playlists: this.playlists,
      onCancelCallback: () => this.hideRecommendationEdit(),
      onSubmitCallback: (id: string, r: RecommendationShort) => this.onSubmitRecommendationEdit(id, r)
    };

    this.editRecommendationModalRef = this.modalService.show(RecommendationEditWizardComponent, {
      initialState,
      backdrop: 'static',
      keyboard: false
    });
  }

  removeRecommendation(recommendation: Recommendation) {
    if (confirm("Do you want to remove this recommendation?")) {
      this.recommendationService.delete(recommendation.id).toPromise()
        .then(() => this.loadById(this.questionnaireId))
        .catch(this.onRejected());
    }
  }

  hideQuestionEdit() {
    this.editQuesitonModalRef.hide();
    this.editQuesitonModalRef = null;
  }

  hideRecommendationEdit() {
    this.editRecommendationModalRef.hide();
    this.editRecommendationModalRef = null;
  }

  onSubmitRecommendationEdit(id: string, recommendation: RecommendationShort) {
    let answer: Promise<any> = id != null
      ? this.recommendationService.update(id, recommendation).toPromise()
      : this.recommendationService.create(this.questionnaireId, recommendation).toPromise();
    answer.then(() => {
      this.hideRecommendationEdit();
      this.loadById(this.questionnaireId)
    }).catch(this.onRejected());
  }

  private arrayShortToFull(short: RecommendationItem[]): Recommendation[] {
    return short.map(sh => this.shortToFull(sh));
  }

  private shortToFull(short: RecommendationItem): Recommendation {
    let fullClauses: RecommendationClause[] = short.info.clauses.map(clause =>
      this.shortClauseToFull(clause)
    );

    let fullExplanations: ExplanationFull[] = short.info.explanations.map(e =>
      this.shortExplanationToFull(e)
    );
    return new Recommendation(fullClauses, fullExplanations, short.info.type, short.id, short.info.description);
  }

  private shortExplanationToFull(short: ExplanationShort): ExplanationFull {
    if (short.type == BY_ANSWER_EXPLANATION_TYPE) {
      return new ExplanationFull(short.type,
        new ExplanationQuestionFull(short.value.question_id,
          short.value.answer_value, short.value.text, short.value.playlist_id)
      );
    }
    if (short.type == TEXT_EXPLANATION_TYPE) {
      return new ExplanationFull(short.type,
        new ExplanationTextFull(short.value)
      );
    }
    return null;
  }

  private shortClauseToFull(short: RecommendationClauseShort): RecommendationClause {
    return new RecommendationClause(
      RECOMMENDATION_SUPPORTED.find(at => at.type == short.answer_type),
      short.value_operator,
      //TODO use regexp templates to expand values back if other type support is needed
      RECOMMENDATION_SUPPORTED_ANSWER_VALUES.find(av => short.value == av.label),
      short.question_operator,
      short.question_ids.map(v => this.questions.find(q => q.id == v))
        .filter(q => q)
    );
  }

  getDescription(recommendation: Recommendation) {
    if (recommendation.description != null && recommendation.description.length > 0) {
      return recommendation.description;
    }
    return recommendation.type;
  }
}



// WEBPACK FOOTER //
// /home/developer/Downloads/SleepRestoreBackend/sleep-restore-boot/src/main/ngapp/src/app/questionnaire-edit/questionnaire-edit.component.ts